import Autocomplete from '@mui/material/Autocomplete';
import Box from '@mui/material/Box';
import Chip from '@mui/material/Chip';
import TextField from '@mui/material/TextField';
import { useRef, useState } from 'react';
import { Controller, useFormContext, useFormState } from 'react-hook-form';
import { FormFieldWrapper } from '../../layout';
import { getErrorByPath } from '../formComponents.utils';
import { COMMIT_TRIGGER } from './comboBox.constants';
import {
  buildChipModel,
  commitFreeSoloValue,
  getComboBoxMode,
  shouldCommitFreeSolo,
  toAutocompleteValue,
  toFieldValue,
} from './comboBox.utils';

/**
 * Controlled ComboBox form field.
 *
 * This component integrates MUI Autocomplete with react-hook-form
 * and supports single/multiple selection with optional freeSolo input.
 *
 * ---
 *
 * ### Value behavior
 *
 * | Mode | RHF field value | Autocomplete value |
 * | ---- | --------------- | ------------------ |
 * | single + options | string | option \| null |
 * | single + freeSolo | string | string |
 * | multiple + options | string[] | option[] |
 * | multiple + freeSolo | string[] | string[] |
 *
 * ---
 *
 * ### FreeSolo commit rules
 *
 * - Blur always commits typed text
 * - Enter commits typed text
 * - Space commits only when `commitOnSpace === true`
 * - Highlighted options are committed ONLY via explicit selection
 *
 * ---
 *
 * ### Duplicate handling
 *
 * - Values must be unique
 * - Comparison is case-sensitive (`===`)
 * - `"foo"` and `"Foo"` are considered different values
 *
 * ---
 *
 * @component
 *
 * @param {Object} props
 * @param {string} props.id - DOM id for accessibility.
 * @param {string} props.name - react-hook-form field name.
 * @param {string} [props.label] - Field label.
 * @param {Array<{label: string, value: string, icon?: string}>} props.options
 * @param {Object} [props.rules] - react-hook-form validation rules.
 * @param {string} [props.placeholder='Select'] - Input placeholder.
 * @param {boolean} [props.freeSolo=false] - Allow custom values.
 * @param {boolean} [props.multiple=false] - Allow multiple values.
 * @param {'row'|'column'} [props.orientation] - Label layout.
 * @param {boolean} [props.fullWidth] - Full width input.
 * @param {boolean} [props.disabled=false] - Disable the field.
 * @param {boolean} [props.commitOnSpace=false] - Commit freeSolo input on space.
 * @param {Object} [props.sx] - MUI sx overrides.
 * @param {boolean} [props.showErrorInline=true]
 * @param {boolean} [props.reserveErrorSpace=false]
 * @param {Object}  [props.wrapperSx]
 * @param {string}  [props.helperText]
 * @param {string}  [props.labelVariant]
 */
export default function ControlledComboBox(props: any) {
  const {
    id,
    name,
    label,
    options = [],
    rules,
    placeholder = 'Select',
    freeSolo = false,
    multiple = false,
    orientation,
    disabled = false,
    sx = {},
    commitOnSpace = false,
    showErrorInline = true,
    reserveErrorSpace = false,
    wrapperSx,
    helperText,
    labelVariant,
    ...rest
  } = props;

  const { control } = useFormContext();
  const mode = getComboBoxMode({ multiple, freeSolo });

  const { errors } = useFormState({ name });
  const error = getErrorByPath(errors, name);

  const errorMessage = showErrorInline ? error?.message : undefined;
  const hasError = showErrorInline ? Boolean(error) : false;

  return (
    <Controller
      name={name}
      control={control}
      rules={rules}
      render={({ field, fieldState }) => {
        const autocompleteValue = toAutocompleteValue({
          fieldValue: field.value,
          options,
          mode,
        });

        const chips = buildChipModel({
          values: Array.isArray(field.value) ? field.value : [],
          options,
          mode,
        });

        return (
          <FormFieldWrapper
            label={label}
            htmlFor={id}
            orientation={orientation}
            labelVariant={labelVariant}
            helperText={helperText}
            error={hasError}
            errorMessage={errorMessage}
            reserveErrorSpace={reserveErrorSpace}
            sx={wrapperSx}
          >
            <ComboBoxInput
              id={id}
              value={autocompleteValue}
              fieldValue={field.value}
              onChange={(value) => field.onChange(value)}
              options={options}
              mode={mode}
              disabled={disabled}
              placeholder={placeholder}
              commitOnSpace={commitOnSpace}
              fieldState={fieldState}
              inputRef={field.ref}
              chips={chips}
              error={hasError}
              sx={sx}
              {...rest}
            />
          </FormFieldWrapper>
        );
      }}
    />
  );
}

/* ----------------------------- */
/* Internal Input Component      */
/* ----------------------------- */
function ComboBoxInput({
  id,
  value,
  fieldValue,
  onChange,
  options,
  mode,
  disabled,
  placeholder,
  commitOnSpace,
  fieldState,
  inputRef,
  chips,
  error,
  sx,
  ...rest
}) {
  const [inputValue, setInputValue] = useState('');
  const highlightedOptionRef = useRef(null);

  const tryCommit = (trigger) => {
    if (
      !shouldCommitFreeSolo({
        trigger,
        hasHighlightedOption: highlightedOptionRef.current != null,
        commitOnSpace,
      })
    ) {
      return;
    }

    const committed = commitFreeSoloValue({
      inputValue,
      currentValue: fieldValue,
      mode,
    });

    if (committed !== null) {
      onChange(committed);
    }

    setInputValue('');
  };

  return (
    <Box
      sx={{
        display: 'flex',
        flexDirection: 'column',
        gap: 1,
        border: '1px solid',
        borderColor: error ? 'error.main' : 'grey.300',
        borderRadius: 2,
      }}
    >
      <Autocomplete
        id={id}
        value={value}
        options={options}
        multiple={mode.includes('multiple')}
        freeSolo={mode.includes('free-solo')}
        disabled={disabled}
        renderTags={() => null}
        inputValue={inputValue}
        onInputChange={(_, v) => setInputValue(v)}
        getOptionLabel={(opt) => (typeof opt === 'string' ? opt : (opt?.label ?? ''))}
        onHighlightChange={(_, option) => {
          highlightedOptionRef.current = option;
        }}
        onClose={() => {
          highlightedOptionRef.current = null;
        }}
        onChange={(_, newValue) => {
          const next = toFieldValue({
            autocompleteValue: newValue,
            mode,
          });
          onChange(next);
          setInputValue('');
        }}
        renderInput={(params) => (
          <TextField
            {...params}
            inputRef={inputRef}
            placeholder={placeholder}
            size='small'
            error={!!fieldState.error}
            onBlur={() => tryCommit(COMMIT_TRIGGER.BLUR)}
            onKeyDownCapture={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
                e.stopPropagation();
                tryCommit(COMMIT_TRIGGER.ENTER);
              }

              if (commitOnSpace && e.code === 'Space') {
                e.preventDefault();
                e.stopPropagation();
                tryCommit(COMMIT_TRIGGER.SPACE);
              }
            }}
            sx={{
              '& .MuiOutlinedInput-root': {
                borderRadius: 0,
                '& .MuiOutlinedInput-notchedOutline': { border: 'none' },
                '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  border: 'none',
                },
              },
            }}
          />
        )}
        sx={{ width: '100%', ...sx }}
        {...rest}
      />

      {chips.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1, p: 1 }}>
          {chips.map((chip) => (
            <Chip
              key={chip.value}
              label={chip.label}
              size='small'
              onDelete={() =>
                onChange(
                  Array.isArray(fieldValue)
                    ? fieldValue.filter((v) => v !== chip.value)
                    : fieldValue,
                )
              }
            />
          ))}
        </Box>
      )}
    </Box>
  );
}
